/**
 * CurrencyInput — input de valor monetário com formatação brasileira (pt-BR).
 *
 * Comportamento:
 *  - Exibição (blur): "1.500.000,50"  — separador de milhar = ".", decimal = ","
 *  - Edição  (focus): mostra o número bruto (ex: "1500000,50") para o usuário editar
 *  - Aceita tanto "," quanto "." como separador decimal ao digitar
 *  - Sempre chama onChange(Number) — valor numérico puro (ponto flutuante JS)
 */
import { useState, type CSSProperties } from "react";

interface CurrencyInputProps {
  value?: number;
  onChange: (value: number) => void;
  style?: CSSProperties;
  placeholder?: string;
  decimals?: number;
}

export function CurrencyInput({
  value,
  onChange,
  style,
  placeholder = "0",
  decimals = 0,
}: CurrencyInputProps) {
  // raw === null → modo exibição (formatado)
  // raw === string → modo edição
  const [raw, setRaw] = useState<string | null>(null);
  const isEditing = raw !== null;

  const handleFocus = () => {
    // Ao focar, exibe o número bruto sem formatação para facilitar edição
    const n = Number(value) || 0;
    setRaw(n === 0 ? "" : String(n).replace(".", ","));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRaw(e.target.value);
  };

  const handleBlur = () => {
    // Converte o que o usuário digitou para número JS
    // Suporta: "1500000", "1.500.000", "1500000,50", "1500000.50"
    const str = (raw || "").trim();
    let num = 0;
    if (str) {
      // Se tem vírgula como separador decimal (brasileiro)
      if (str.includes(",")) {
        // Remove pontos de milhar e troca vírgula por ponto
        num = parseFloat(str.replace(/\./g, "").replace(",", ".")) || 0;
      } else {
        // Apenas ponto (ou sem separador decimal)
        num = parseFloat(str.replace(/[^\d.]/g, "")) || 0;
      }
    }
    setRaw(null);
    onChange(num);
  };

  // Valor para exibição
  const displayValue = isEditing
    ? (raw ?? "")
    : (Number(value) || 0) === 0
      ? ""
      : Number(value).toLocaleString("pt-BR", {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        });

  return (
    <input
      type="text"
      inputMode="decimal"
      value={displayValue}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
      placeholder={placeholder}
      style={style}
    />
  );
}

export default CurrencyInput;
